import Button from '../Input2/Button';

export interface CallOutcomeOption {
  value: string;
  label: string;
}

/**
 * The canonical call dispositions, shared so the in-call softphone and the call-step
 * "Mark as done" picker stay in lockstep with the server's `callOutcomeSchema`.
 */
export const CALL_OUTCOMES: ReadonlyArray<CallOutcomeOption> = [
  { value: 'connected', label: 'Connected' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'busy', label: 'Busy' },
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'callback', label: 'Callback' },
  { value: 'other', label: 'Other' },
];

export interface CallOutcomePickerProps {
  /** The dispositions to offer. Defaults to {@link CALL_OUTCOMES}. */
  outcomes?: ReadonlyArray<CallOutcomeOption>;
  /** Prompt shown above the grid. */
  title?: string;
  onSelect: (value: string) => void;
  /** When provided, renders a "Skip" affordance below the grid. */
  onSkip?: () => void;
  /** Disable every button while a selection is being saved. */
  disabled?: boolean;
  className?: string;
}

/**
 * Pure "How did it go?" disposition grid. Shared by the in-call softphone (post-hangup) and the
 * call-step "Mark as done" popup so both log the same set of outcomes with identical styling.
 */
export function CallOutcomePicker({
  outcomes = CALL_OUTCOMES,
  title = 'How did it go?',
  onSelect,
  onSkip,
  disabled,
  className,
}: CallOutcomePickerProps) {
  return (
    <div className={className}>
      <span className="text-center text-sm text-gray-500 dark:text-gray-400">{title}</span>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {outcomes.map((outcome) => (
          <Button
            key={outcome.value}
            size="sm"
            tone="outline"
            disabled={disabled}
            onClick={() => onSelect(outcome.value)}>
            {outcome.label}
          </Button>
        ))}
      </div>
      {onSkip && (
        <Button className="mt-2 w-full" size="sm" text disabled={disabled} onClick={onSkip}>
          Skip
        </Button>
      )}
    </div>
  );
}
